/**
 * Cart Payment Methods Controller
 * Manages payment method visibility based on cart purchase type
 */

// Global state management
let cartPaymentOptionWrapper = null;
let cartDataForPaymentSelectionInCart = null;
let cartAvailablePaymentMethods = {
  one_time: [],
  subscription: [],
  mixed: [],
};
let cartSelectedPaymentMethod = null;

/**
 * Load current cart data
 */
async function loadcartDataForPaymentSelectionInCart() {
  try {
    const response = await fetch("/cart.js");
    cartDataForPaymentSelectionInCart = await response.json();

    // Always update the global reference
    window.cartDataForPaymentSelectionInCart = cartDataForPaymentSelectionInCart;
  } catch (error) {
    console.error('🛒 Error loading cart data:', error);
    cartDataForPaymentSelectionInCart = { items: [], attributes: {} };
  }
}

/**
 * Get current cart purchase type (mini cart uses internal cart data; section can pass items)
 * @param {Array} [cartItemsOverride] - Optional items array (e.g. from checkout section); else uses cartDataForPaymentSelectionInCart
 * @returns {'onetime'|'subscription'|'mixed'}
 */
function getCartPurchaseType(cartItemsOverride) {
  return analyzeCartItems(cartItemsOverride);
}

/**
 * Analyze cart items to determine purchase type.
 * Same logic for mini cart and checkout section; accepts optional items so section can pass cart items.
 * @param {Array} [cartItemsOverride] - Optional items array; if omitted, uses cartDataForPaymentSelectionInCart.items
 * @returns {'onetime'|'subscription'|'mixed'}
 */
function analyzeCartItems(cartItemsOverride) {
  const cartItems = cartItemsOverride ?? cartDataForPaymentSelectionInCart?.items;

  if (!cartItems || cartItems.length === 0) {
    return 'onetime';
  }
  
  const subscriptionCount = cartItems.filter((item) => {
    // Check if any of the three subscription criteria are satisfied
    return (
      // 1. Check properties._subscriptionType
      (item.properties &&
        item.properties._subscriptionType &&
        item.properties._subscriptionType !== "") ||
      // 2. Check properties._sellingplan
      (item.properties &&
        item.properties._sellingplan &&
        item.properties._sellingplan !== "") ||
      // 3. Check selling_plan_allocation
      (item.selling_plan_allocation &&
        item.selling_plan_allocation.selling_plan)
    );
  }).length;
  const onetimeCount = cartItems.length - subscriptionCount;
  
  if (subscriptionCount > 0 && onetimeCount === 0) {
    return 'subscription';
  } else if (subscriptionCount === 0 && onetimeCount > 0) {
    return 'onetime';
  } else if (subscriptionCount > 0 && onetimeCount > 0) {
    return 'mixed';
  }
  
  return 'onetime'; // Default fallback
}

/**
 * Update cart _newFlow attribute
 */
async function updateCartAttribute(newFlowValue, availablePayments) {
  try {
    const response = await fetch("/cart/update.js", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
      },
      body: JSON.stringify({
        attributes: {
          _newFlow: newFlowValue,
          ...(availablePayments ? {_availablePayments: availablePayments} : {}),
        },
      }),
    });

    if (response.ok) {
      const updatedCart = await response.json();
      cartDataForPaymentSelectionInCart = updatedCart;
    } else {
      console.error(
        "Failed to update cart _newFlow attribute:",
        response.statusText
      );
    }
  } catch (error) {
    console.error("Error updating cart _newFlow attribute:", error);
  }
}

/**
 * Update cart _paymentMethod attribute and manage selling plan allocations
 */
async function updateCartPaymentMethodAttribute(allowedPaymentMethodValue) {
  const selectedMethod = cartPaymentOptionWrapper?.querySelector('input[name="payment-method"]:checked');
  
  if (!selectedMethod && !allowedPaymentMethodValue) {
    console.log('🛒 No payment method selected');
    return;
  }
  
  const paymentMethodValue = selectedMethod ? selectedMethod.value : allowedPaymentMethodValue;

  if (cartSelectedPaymentMethod === paymentMethodValue) {
    console.log("🛒 Payment method already selected");
    return;
  }
  
  try {
    // First, update the payment method attribute
    const response = await fetch('/cart/update.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json'
      },
      body: JSON.stringify({
        attributes: {
          _paymentMethod: paymentMethodValue
        }
      })
    });
    
    if (response.ok) {
      const updatedCart = await response.json();
      
      // Update local cart data
      cartDataForPaymentSelectionInCart = updatedCart;
      cartSelectedPaymentMethod = paymentMethodValue;
      
      // Now handle selling plan allocations based on payment method
      await handleSellingPlanAllocations(paymentMethodValue);

      // Refresh cart display after payment method change
      if (typeof window.refreshCartPaymentChange === "function") {
        await window.refreshCartPaymentChange();
      }
      
    } else {
      console.error('Failed to update cart _paymentMethod attribute:', response.statusText);
    }
  } catch (error) {
    console.error('Error updating cart _paymentMethod attribute:', error);
  }
}

/**
 * Handle selling plan allocations based on payment method
 */
async function handleSellingPlanAllocations(paymentMethodValue) {
  if (!cartDataForPaymentSelectionInCart || !cartDataForPaymentSelectionInCart.items) {
    console.log('🛒 No cart data available for selling plan management');
    return;
  }
    
  // Get subscription products based on _subscriptionType property
  const subscriptionItems = cartDataForPaymentSelectionInCart.items.filter(item =>{
    // Check if any of the three subscription criteria are satisfied
    return (
      // 1. Check properties._subscriptionType
      (item.properties &&
        item.properties._subscriptionType &&
        item.properties._subscriptionType !== "") ||
      // 2. Check properties._sellingplan
      (item.properties &&
        item.properties._sellingplan &&
        item.properties._sellingplan !== "") ||
      // 3. Check selling_plan_allocation
      (item.selling_plan_allocation &&
        item.selling_plan_allocation.selling_plan)
    );
  });
  
  if (subscriptionItems.length === 0) {
    console.log('🛒 No subscription items found in cart');
    return;
  }
  
  const isManualPayment = KROWN.settings.manualPaymentTypes.includes(paymentMethodValue);
  
  if (isManualPayment) {
    // Non-card payment: Remove selling plan allocations but store the ID first

    for (const item of subscriptionItems) {
      if (item.selling_plan_allocation) {

        // Store the selling plan ID in properties and remove selling plan allocation
        const sellingPlanId = `${item.selling_plan_allocation.selling_plan.id}`;
        await updateCartItem(item.key, {
          properties: { _sellingplan: sellingPlanId },
          sellingPlanId: null,
        });

      }
    }
  } else {
    // Card payment: Ensure subscription products have selling plan allocation
    for (const item of subscriptionItems) {
      if (!item.selling_plan_allocation) {

        // Use stored selling plan ID from properties if available, otherwise get first available
        let sellingPlanId = null;
        if (item.properties && item.properties._sellingplan) {
          sellingPlanId = item.properties._sellingplan;
        }

        if (sellingPlanId) {
          // Add selling plan allocation and remove the _sellingplan property
          await updateCartItem(item.key, {
            sellingPlanId: sellingPlanId,
            properties: { _sellingplan: "" }, // Remove the property
          });
        }
      }
    }
  }
}

/**
 * Update cart item (properties and/or selling plan)
 */
async function updateCartItem(itemKey, options = {}) {
  try {
    const currentItem = cartDataForPaymentSelectionInCart.items.find(item => item.key === itemKey);
    if (!currentItem) {
      console.log('Item not found for update:', itemKey);
      return;
    }
    
    const updateData = {
      id: itemKey,
      quantity: currentItem.quantity
    };
    
    // Handle properties update
    if (options.properties) {
      const updatedProperties = {
        ...currentItem.properties
      };
      
      // Process each property - remove if value is null, otherwise add/update
      Object.entries(options.properties).forEach(([key, value]) => {
        if (value === null) {
          delete updatedProperties[key];
        } else {
          updatedProperties[key] = value;
        }
      });
      
      updateData.properties = updatedProperties;
    }
    
    // Handle selling plan update
    if (options.sellingPlanId !== undefined) {
      updateData.selling_plan = options.sellingPlanId || null;
    }
    
    const response = await fetch('/cart/change.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json'
      },
      body: JSON.stringify(updateData)
    });
    
    if (response.ok) {
      const updatedCart = await response.json();
      cartDataForPaymentSelectionInCart = updatedCart;
      
    } else {
      console.error('Failed to update item:', itemKey, response.statusText);
    }
  } catch (error) {
    console.error('Error updating item:', itemKey, error);
  }
}

/**
 * Ensure a valid payment method is selected after hiding options
 */
async function ensureValidPaymentMethodSelected(paymentMethodValue) {
  const selectedMethod = cartPaymentOptionWrapper?.querySelector('input[name="payment-method"]:checked');
  
  // If selected method is now hidden/disabled, select the first available one
  if (
    (selectedMethod &&
      (selectedMethod.disabled ||
        selectedMethod
          .closest(".payment-method__option")
          .classList.contains("display-none"))) ||
    (cartPaymentOptionWrapper && !selectedMethod)
  ) {
    const firstAvailable = [
      ...cartPaymentOptionWrapper.querySelectorAll(
        'input[name="payment-method"]:not([disabled])'
      ),
    ].find((input) => !input.closest('.payment-method__option').classList.contains('display-none'));
    if (firstAvailable) {
      firstAvailable.checked = true;
    }
  }
  // Update cart attribute when auto-selecting
  await updateCartPaymentMethodAttribute(paymentMethodValue);
}

/**
 * Update payment method visibility based on cart purchase type
 */
async function updatePaymentMethods() {
  const showPaymentInCart = KROWN.settings.payment_method_selection.should_show_in_cart;
  const purchaseType = getCartPurchaseType();

  if (cartPaymentOptionWrapper) filterAndShowPaymentMethods(purchaseType);

  // Handle empty cart - hide entire payment method container
  if (!cartDataForPaymentSelectionInCart || cartDataForPaymentSelectionInCart.items.length === 0) {
    if (cartPaymentOptionWrapper) {
      cartPaymentOptionWrapper.style.display = "none";
    }
    showCheckoutButtons()
    
    return;
  }
  // Handle no payment method configured - hide entire payment method container
  if (cartAvailablePaymentMethods.one_time.length === 0 && cartAvailablePaymentMethods.subscription.length === 0 && cartAvailablePaymentMethods.mixed.length === 0  ) {
    if (cartPaymentOptionWrapper) {
      cartPaymentOptionWrapper.style.display = "block";
      showNoPaymentMethodsMessage();
    }
    hideCheckoutButtons();

    return;
  }
  // Handle no payment method configured for onetime - hide entire payment method container
  if (purchaseType === "onetime" && cartAvailablePaymentMethods.one_time.length === 0) {
    if (cartPaymentOptionWrapper) {
      cartPaymentOptionWrapper.style.display = "block";
      showNoPaymentMethodsMessage();
    }
    hideCheckoutButtons();
    return;
  }

  // Handle no payment method configured for subscription - hide entire payment method container  
  if (purchaseType === "subscription" && cartAvailablePaymentMethods.subscription.length === 0) {
    if (cartPaymentOptionWrapper) {
      cartPaymentOptionWrapper.style.display = "block";
      showNoPaymentMethodsMessage();
    }
    hideCheckoutButtons();
    return;
  }
  // Handle no payment method configured for mixed - hide entire payment method container
  if (purchaseType === "mixed" && cartAvailablePaymentMethods.mixed.length === 0) {
    if (cartPaymentOptionWrapper) {
      cartPaymentOptionWrapper.style.display = "block";
      showNoPaymentMethodsMessage();
    }
    hideCheckoutButtons();
    return;
  }

  // hide the no payment methods message if it is shown
  hideNoPaymentMethodsMessage();

  //  Hide payment selection if onetime + only Credit/Debit Card available
  if (purchaseType === "onetime") {
    // Hide the payment method container if it exists
    if (cartPaymentOptionWrapper) {
      cartPaymentOptionWrapper.style.display = "none";
    }

    const oneTimeMethods = cartAvailablePaymentMethods.one_time || [];
    const hasSingleMethod = oneTimeMethods.length === 1;
    const selectedMethodOntime = hasSingleMethod ? oneTimeMethods[0] : undefined;

    // Ensure a valid payment method is selected
    await ensureValidPaymentMethodSelected(selectedMethodOntime);

    // Update cart attribute based on payment methods count
    await updateCartAttribute(!hasSingleMethod, oneTimeMethods);

    // Show checkout buttons
    showCheckoutButtons();

    return;
  }

  // Condition 2: Hide payment selection if subscription/mixed + only Credit/Debit Card available
  if (
      (purchaseType === "subscription" && cartAvailablePaymentMethods.subscription.length === 1) || 
      (purchaseType === "mixed" && cartAvailablePaymentMethods.mixed.length === 1)
  ) {
    
    await updateCartAttribute(
      false,
      purchaseType === "subscription"
      ? cartAvailablePaymentMethods.subscription
      : cartAvailablePaymentMethods.mixed
    );
    if (cartSelectedPaymentMethod) showCheckoutButtons();
    
    const singleMethodPaymentType =
      purchaseType === "subscription"
        ? cartAvailablePaymentMethods.subscription[0]
        : cartAvailablePaymentMethods.mixed[0];

      await ensureValidPaymentMethodSelected(singleMethodPaymentType);

      if (cartPaymentOptionWrapper) {
        cartPaymentOptionWrapper.style.display = "none";
      }else{
        await handleSellingPlanAllocations(singleMethodPaymentType);
      }
      
      return;
  }

  // Handle subscription/mixed payments for Mixed market
  if (
    (purchaseType === "subscription" || purchaseType === "mixed") &&
    showPaymentInCart
  ) {
    const purchaseTypePaymentList = cartAvailablePaymentMethods[purchaseType] || [];
    const existingPaymentAttributeValue = cartDataForPaymentSelectionInCart.attributes?._paymentMethod;
    let fallbackMethod;

    if (
      existingPaymentAttributeValue &&
      purchaseTypePaymentList.includes(existingPaymentAttributeValue)
    ) {
      fallbackMethod = existingPaymentAttributeValue;
    } else {
      const onlineIndex = purchaseTypePaymentList.findIndex(
        (method) => method === "Online"
      );

      fallbackMethod =
        onlineIndex !== -1
          ? purchaseTypePaymentList[onlineIndex]
          : purchaseTypePaymentList[0];
    }

    // Ensure a valid payment method is selected
    await ensureValidPaymentMethodSelected(
      cartPaymentOptionWrapper ? undefined : fallbackMethod
    );
    
    // Update cart attribute _newFlow to false
    await updateCartAttribute(false, purchaseTypePaymentList);

    if (cartPaymentOptionWrapper) {
      cartPaymentOptionWrapper.style.display = "block";
      showCheckoutButtons();
    } else {
      hideCheckoutButtons();
    }
    return;
  }

  // Hide checkout button and show no payment methods message if subscription/mixed have mixed payment and not configured to show in cart
  if (
    ((purchaseType === "subscription" && cartAvailablePaymentMethods.subscription.length > 1) || (purchaseType === "mixed" && cartAvailablePaymentMethods.mixed.length > 1)) &&
    !showPaymentInCart
  ) {    
    if (cartPaymentOptionWrapper) {
      const allPaymentMethods = cartPaymentOptionWrapper.querySelectorAll(".payment-method__option");
      allPaymentMethods.forEach(method => {
        method.classList.add("display-none");
      });
      cartPaymentOptionWrapper.style.display = "block";
      showNoPaymentMethodsMessage();
    }
     hideCheckoutButtons();
     return;
  }
}

/**
 * Setup listeners for cart updates and payment method changes
 */
function setupCartListeners() {
  // Listen for payment method changes
  if (cartPaymentOptionWrapper) {
    const paymentMethodInputs = cartPaymentOptionWrapper.querySelectorAll('input[name="payment-method"]');
    paymentMethodInputs.forEach(input => {
      input.addEventListener('change', async (e) => {
        const cartForm = document.querySelector("form#cart.cart__form");
        // Add the "processing" class
        document.getElementById("page-loader").classList.remove("hidden");
        if (cartForm) {
          cartForm.classList.add("processing");
        }
        hideCheckoutButtons();
        await updateCartPaymentMethodAttribute();
        showCheckoutButtons();
        if (cartForm) {
          cartForm.classList.remove("processing");
        }
        document.getElementById("page-loader").classList.add("hidden");
      });
    });
  }
}

/**
 * Initialize cart payment methods controller
 */
async function initCartPaymentMethods() {
  try {
    // Get payment method container
    cartPaymentOptionWrapper = document.querySelector('.payment-method-cart');
    if(cartPaymentOptionWrapper){
      document.getElementById("page-loader").classList.remove("hidden");
    }

    cartAvailablePaymentMethodsFromSettings();

    // Initial setup
    await loadcartDataForPaymentSelectionInCart()

    if(cartPaymentOptionWrapper){
        // Set initial payment method selection based on cart attribute
        const currentPaymentMethod = cartDataForPaymentSelectionInCart?.attributes?._paymentMethod;
        if (currentPaymentMethod) {
          const paymentMethodInput = cartPaymentOptionWrapper.querySelector(`input[name="payment-method"][value="${currentPaymentMethod}"]`);
          if (paymentMethodInput) {
            paymentMethodInput.checked = true;
          }
        }
    }

    await updatePaymentMethods();


    // Setup listeners
    setupCartListeners();

    if (cartPaymentOptionWrapper) {
      document.getElementById("page-loader").classList.add("hidden");
    }
  } finally {
    window.cartPaymentMethodsInitialized = true;
    document.dispatchEvent(new CustomEvent('cartPaymentMethodsReady'));
  }
}

/**
 * Public method to manually refresh payment methods
 */
async function refreshCartPaymentMethods() {
  await loadcartDataForPaymentSelectionInCart();
  await updatePaymentMethods();
}

function filterAndShowPaymentMethods(purchaseType) {
  const allPaymentMethods = cartPaymentOptionWrapper.querySelectorAll(".payment-method__option");
  allPaymentMethods.forEach(method => {
    method.classList.add("display-none");
  });

  if(purchaseType === "onetime"){
    cartAvailablePaymentMethods.one_time.forEach(paymentMethod => {
      const paymentMethodElement = cartPaymentOptionWrapper.querySelector(`input[name="payment-method"][value="${paymentMethod}"]`);
      if(paymentMethodElement){
        const labelElement = paymentMethodElement.closest('.payment-method__option'); // Get the label
        if(labelElement){
          labelElement.classList.remove("display-none"); // This targets the label
        }
      }
    });
  }else if(purchaseType === "subscription"){
    cartAvailablePaymentMethods.subscription.forEach(paymentMethod => {
      const paymentMethodElement = cartPaymentOptionWrapper.querySelector(`input[name="payment-method"][value="${paymentMethod}"]`);
      if(paymentMethodElement){
        const labelElement = paymentMethodElement.closest('.payment-method__option'); // Get the label
        if(labelElement){
          labelElement.classList.remove("display-none"); // This targets the label
        }
      }
    }); 
    
  }else if(purchaseType === "mixed"){
    cartAvailablePaymentMethods.mixed.forEach(paymentMethod => {
      const paymentMethodElement = cartPaymentOptionWrapper.querySelector(`input[name="payment-method"][value="${paymentMethod}"]`);
      if(paymentMethodElement){
        const labelElement = paymentMethodElement.closest('.payment-method__option'); // Get the label
        if(labelElement){
          labelElement.classList.remove("display-none"); // This targets the label
        }
      }
    });
  }

}

function cartAvailablePaymentMethodsFromSettings () {
  const {
    gocardless_countries_one_time,
    gocardless_countries_subscription,
    gocardless_countries_mixed,
    online_countries_one_time,
    online_countries_subscription,
    online_countries_mixed,
    klarna_countries_one_time,
    klarna_countries_subscription,
    klarna_countries_mixed,
    banktransfer_countries_one_time,
    banktransfer_countries_subscription,
    banktransfer_countries_mixed,
    bancontact_countries_one_time,
    bancontact_countries_subscription,
    bancontact_countries_mixed,
    ideal_countries_one_time,
    ideal_countries_subscription,
    ideal_countries_mixed,
    tranzila_countries_one_time,
    tranzila_countries_subscription,
    tranzila_countries_mixed,
    cashcheque_countries_one_time,
    cashcheque_countries_subscription,
    cashcheque_countries_mixed,
    blik_countries_one_time,
    blik_countries_subscription,
    blik_countries_mixed,
  } = KROWN.settings.available_payment_methods;
  const country = KROWN.settings.current_country_code;


  // Helper: check if a setting includes the current country
  function includesCountry(setting) {
    if (!setting) return false;
    return setting.split(",").map(c => c.trim()).includes(country);
  }

  const paymentMethods = {
    one_time: [],
    subscription: [],
    mixed: [],
  };

  // One-time
  if (includesCountry(gocardless_countries_one_time)) {
    paymentMethods.one_time.push("gocardless");
  }
  if (includesCountry(online_countries_one_time)) {
    paymentMethods.one_time.push("Online");
  }
  if (includesCountry(klarna_countries_one_time)) {
    paymentMethods.one_time.push("klarna");
  }
  if (includesCountry(banktransfer_countries_one_time)) {
    paymentMethods.one_time.push("banktransfer");
  }
  if (includesCountry(bancontact_countries_one_time)) {
    paymentMethods.one_time.push("bancontact");
  }
  if (includesCountry(ideal_countries_one_time)) {
    paymentMethods.one_time.push("ideal");
  }
  if (includesCountry(tranzila_countries_one_time)) {
    paymentMethods.one_time.push("tranzila");
  }
  if (includesCountry(cashcheque_countries_one_time)) {
    paymentMethods.one_time.push("cashcheque");
  }
  if (includesCountry(blik_countries_one_time)) {
    paymentMethods.one_time.push("blik");
  }

  // Subscription
  if (includesCountry(gocardless_countries_subscription)) {
    paymentMethods.subscription.push("gocardless");
  }
  if (includesCountry(online_countries_subscription)) {
    paymentMethods.subscription.push("Online");
  }
  if (includesCountry(klarna_countries_subscription)) {
    paymentMethods.subscription.push("klarna");
  } 
  if (includesCountry(banktransfer_countries_subscription)) {
    paymentMethods.subscription.push("banktransfer");
  }
  if (includesCountry(bancontact_countries_subscription)) {
    paymentMethods.subscription.push("bancontact");
  }
  if (includesCountry(ideal_countries_subscription)) {
    paymentMethods.subscription.push("ideal");
  }
  if (includesCountry(tranzila_countries_subscription)) {
    paymentMethods.subscription.push("tranzila");
  }
  if (includesCountry(cashcheque_countries_subscription)) {
    paymentMethods.subscription.push("cashcheque");
  }
  if (includesCountry(blik_countries_subscription)) {
    paymentMethods.subscription.push("blik");
  }

  // Mixed
  if (includesCountry(gocardless_countries_mixed)) {
    paymentMethods.mixed.push("gocardless");
  }
  if (includesCountry(online_countries_mixed)) {
    paymentMethods.mixed.push("Online");
  }
  if (includesCountry(klarna_countries_mixed)) {
    paymentMethods.mixed.push("klarna");
  } 
  if (includesCountry(banktransfer_countries_mixed)) {
    paymentMethods.mixed.push("banktransfer");
  }
  if (includesCountry(bancontact_countries_mixed)) {
    paymentMethods.mixed.push("bancontact");
  }
  if (includesCountry(ideal_countries_mixed)) {
    paymentMethods.mixed.push("ideal");
  }
  if (includesCountry(tranzila_countries_mixed)) {
    paymentMethods.mixed.push("tranzila");
  }
  if (includesCountry(cashcheque_countries_mixed)) {
    paymentMethods.mixed.push("cashcheque");
  }
  if (includesCountry(blik_countries_mixed)) {
    paymentMethods.mixed.push("blik");
  }

  cartAvailablePaymentMethods = paymentMethods;
  window.cartAvailablePaymentMethods = cartAvailablePaymentMethods;

}

// Function to hide checkout buttons from mini cart
function hideCheckoutButtons() {
  const customCheckoutButton = document.getElementById(
    "custom-checkout-button"
  );
  const guestCheckoutButton = document.getElementById("CheckOut");
  const loader = document.querySelector(".cart-buttons-loader");
  const buttonsContainer = document.querySelector(".flex-buttons.mini-cart-buttons-container");

  if (customCheckoutButton) {
    customCheckoutButton.classList.add("display-none");
  }

  if (guestCheckoutButton) {
    guestCheckoutButton.classList.add("display-none");
  }

  if (loader) loader.style.display = "none";
  if (buttonsContainer) buttonsContainer.style.display = "";
  const sectionEl = document.querySelector("[data-checkout-buttons-section]");
  if (sectionEl && typeof window.hideCheckoutButtoninCheckoutSection === "function") {
    console.log('hideCheckoutButtoninCheckoutSection called');
    window.hideCheckoutButtoninCheckoutSection();
  }
}

// Function to show checkout buttons from mini cart
function showCheckoutButtons() {
    const customLoader = document.getElementById("custom-button-loader");
    const customText = document.getElementById("custom-button-text");
    const guestLoader = document.getElementById("guest-button-loader");
    const guestText = document.querySelector(".guest-user-button-text");
    const loader = document.querySelector(".cart-buttons-loader");
    const buttonsContainer = document.querySelector(".flex-buttons.mini-cart-buttons-container");

    const customCheckoutButton = document.getElementById(
      "custom-checkout-button"
    );
    const guestCheckoutButton = document.getElementById("CheckOut");

    if (customLoader && customText) {
      customLoader.classList.add("display-none");
      customText.classList.remove("display-none");
      customCheckoutButton.disabled = false;
    }

    if (guestLoader && guestText) {
      guestLoader.classList.add("display-none");
      guestText.classList.remove("display-none");
      guestCheckoutButton.disabled = false;
    }
    if (loader) loader.style.display = "none";
    if (buttonsContainer) buttonsContainer.style.display = "";
    if (document.querySelector("[data-checkout-buttons-section]") && typeof window.showCheckoutButtoninCheckoutSection === "function") {
      console.log('showCheckoutButtoninCheckoutSection called');
      window.showCheckoutButtoninCheckoutSection();
    }
}

function showNoPaymentMethodsMessage() {
  const message = cartPaymentOptionWrapper?.querySelector(
    ".payment-method-message"
  );
  if (message) {
    message.style.display = "block";
  }
}

function hideNoPaymentMethodsMessage() {
  const message = cartPaymentOptionWrapper?.querySelector(
    ".payment-method-message"
  );
  if (message) {
    message.style.display = "none";
  }
}

let cartPaymentMethodsInitialized = false;
function safeInitCartPaymentMethods() {
  if (cartPaymentMethodsInitialized) return;
  cartPaymentMethodsInitialized = true;
  initCartPaymentMethods();
}

if (window.location.pathname.includes("/cart")) {
  document.addEventListener("customCartLoaded", () => {
    safeInitCartPaymentMethods();
  });
} else {
  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", () => {
      safeInitCartPaymentMethods();
    });
  } else {
    safeInitCartPaymentMethods();
  }
}

// Export functions for global access
window.refreshCartPaymentMethods = refreshCartPaymentMethods;
window.getCartPurchaseType = getCartPurchaseType;
window.updatePaymentMethods = updatePaymentMethods;
